<script>
  // Shared current-positions table (U5 / R4-R5). Shown on the Live & Paper dashboard's Overview tab in place of
  // the trades blotter (which lives on the dedicated Trades tab). Fed the strategy's open positions from
  // GET /api/autotrader/{id}/positions (StrategyPosition[]: symbol, qty, avgEntry, markPrice?, unrealizedPnl?).
  // Mirrors TradesTable's structure/styling and symbol formatting.
  import { formatCurrency } from '../../lib/store.js';
  import { formatQuoteAmount } from '../../lib/quoteCurrency.js';
  import { fromRequestSymbol } from '../Backtesting/SymbolMultiSelect.svelte';
  import { getCryptoIconOrLetters, getCryptoGradient } from '../../lib/cryptoIcons.js';

  // null = not yet loaded (show skeleton); [] = loaded with no open positions (show empty state).
  export let positions = null;
  export let loading = false;
  // The strategy's available USD (cash) balance — shown emphasized in the header. null/undefined → hidden.
  export let usdBalance = null;
  // ADR-0014: the currency the cash balance is denominated in (the strategy's quote currency). USD default.
  export let cashCurrency = 'USD';

  $: isLoading = loading || positions === null;
  $: rows = Array.isArray(positions) ? positions : [];
  $: hasUsdBalance = usdBalance !== null && usdBalance !== undefined && !Number.isNaN(Number(usdBalance));

  function displaySymbol(sym) {
    return fromRequestSymbol(sym ?? '');
  }
  function symbolBase(sym) {
    return displaySymbol(sym).split('/')[0];
  }
  // The quote currency (the asset the position is valued/settled in), e.g. BTC-USD → USD.
  function quoteCurrency(sym) {
    const parts = displaySymbol(sym).split('/');
    return (parts[1] ?? '').toUpperCase();
  }
  // The display symbol for a quote currency. USD + USD-pegged stablecoins render as '$'; otherwise the code.
  const QUOTE_SYMBOLS = { USD: '$', USDC: '$', USDT: '$', EUR: '€', GBP: '£', JPY: '¥', BTC: '₿', ETH: 'Ξ' };
  // The position's quote amount = |qty| × current value-per-unit (mark, else avg entry). null when neither
  // is known. This is what the position is worth right now in the quote currency.
  function quoteAmount(p) {
    const unit = p.markPrice ?? p.avgEntry;
    if (unit === null || unit === undefined) return null;
    return Math.abs(Number(p.qty)) * Number(unit);
  }
  // Format an amount in the symbol's quote currency: a known stablecoin/fiat symbol prefixes the number; an
  // unmapped crypto quote suffixes its code (e.g. "0.50 BTC"). Falls back to em-dash on null.
  function fmtQuoteAmount(p) {
    const amount = quoteAmount(p);
    if (amount === null) return '—';
    const cur = quoteCurrency(p.symbol);
    const sym = QUOTE_SYMBOLS[cur];
    const num = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return sym ? `${sym}${num}` : `${num} ${cur}`.trim();
  }
  function symbolIcon(sym) {
    return getCryptoIconOrLetters(symbolBase(sym));
  }
  function symbolGradient(sym) {
    return getCryptoGradient(symbolBase(sym));
  }

  // qty is signed: positive long, negative short.
  function sideLabel(qty) {
    return Number(qty) < 0 ? 'Short' : 'Long';
  }
  function sideClass(qty) {
    return Number(qty) < 0 ? 'side-sell' : 'side-buy';
  }

  // Mark / unrealized P&L are nullable (no mark available → entry-only). Render an em-dash for null.
  function fmtOrDash(value) {
    return value === null || value === undefined ? '—' : formatCurrency(value);
  }
  // Unrealized P&L with an EXPLICIT sign prefix (not color-only — colorblind accessibility) + pos/neg color.
  function fmtPnl(value) {
    if (value === null || value === undefined) return '—';
    const n = Number(value);
    const sign = n > 0 ? '+' : n < 0 ? '−' : '';
    return `${sign}${formatCurrency(Math.abs(n))}`;
  }
  function pnlClass(value) {
    if (value === null || value === undefined) return '';
    const n = Number(value);
    return n > 0 ? 'pnl-pos' : n < 0 ? 'pnl-neg' : '';
  }
</script>

<div class="bt-trades pt-positions">
  <h3 class="bt-trades-title">
    Current Positions
    {#if hasUsdBalance}
      <span class="pt-usd-balance" title="Available cash for this strategy (allocated cash not currently deployed in positions)">(<span class="pt-usd-amount">{formatQuoteAmount(usdBalance, cashCurrency)}</span> {cashCurrency} available)</span>
    {/if}
  </h3>
  {#if isLoading}
    <div class="at-skeleton-rows"><div class="at-skeleton-row"></div></div>
  {:else if rows.length === 0}
    <div class="empty-state">
      <h4 class="empty-state-title">No open positions</h4>
      <p class="empty-state-description">Positions appear once the strategy's first order fills.</p>
    </div>
  {:else}
    <div class="bt-trades-table" role="table" aria-label="Current positions">
      <div class="bt-trades-head" role="row">
        <span role="columnheader">Symbol</span>
        <span role="columnheader">Side</span>
        <span role="columnheader" class="num">Qty</span>
        <span role="columnheader" class="num">Avg Entry</span>
        <span role="columnheader" class="num">Mark</span>
        <span role="columnheader" class="num">Amount</span>
        <span role="columnheader" class="num">Unrealized P&L</span>
      </div>
      <div class="bt-trades-body">
        {#each rows as p, i (p.symbol ?? i)}
          {@const icon = symbolIcon(p.symbol)}
          <div class="bt-trades-row" role="row">
            <span role="cell" class="bt-symbol-cell">
              <span
                class="bt-symbol-icon"
                class:icon-symbol={icon.type === 'icon'}
                style="background: {symbolGradient(p.symbol)}"
                aria-hidden="true"
              >{icon.value}</span>
              <span class="bt-symbol-label">{displaySymbol(p.symbol)}</span>
            </span>
            <span role="cell" class="bt-side {sideClass(p.qty)}">{sideLabel(p.qty)}</span>
            <span role="cell" class="num">{Math.abs(Number(p.qty))}</span>
            <span role="cell" class="num">{formatCurrency(p.avgEntry)}</span>
            <span role="cell" class="num">{fmtOrDash(p.markPrice)}</span>
            <span role="cell" class="num">{fmtQuoteAmount(p)}</span>
            <span role="cell" class="num {pnlClass(p.unrealizedPnl)}">{fmtPnl(p.unrealizedPnl)}</span>
          </div>
        {/each}
      </div>
    </div>
  {/if}
</div>

<style>
  @import '../Backtesting/BacktestResults.css';
  .pnl-pos { color: var(--color-success, #16a34a); font-variant-numeric: tabular-nums; }
  .pnl-neg { color: var(--color-danger, #dc2626); font-variant-numeric: tabular-nums; }
  /* The strategy's available USD balance, emphasized in the Current Positions header. */
  .pt-usd-balance {
    margin-left: 0.5rem;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--text-muted, #8a8b8d);
  }
  .pt-usd-amount {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--color-success, #16a34a);
  }
  /* Positions has its own 7 columns (Symbol, Side, Qty, Avg Entry, Mark, Amount, Unrealized P&L) — set the
     grid explicitly so it isn't coupled to the shared TradesTable column widths. */
  .pt-positions :global(.bt-trades-head),
  .pt-positions :global(.bt-trades-row) {
    grid-template-columns: 1.4fr 0.7fr 1fr 1fr 1fr 1.1fr 1.2fr;
  }
</style>
